/**
 * useProgressiveReply (#468) — React shim around `replyQueue.js`.
 *
 * Drop-in replacement for `const [reply, setReply] = useState('')` in
 * TabletApp. Adds an `enqueueChunk(text)` method for `reply_chunk` WS
 * events; everything else (toast messages, "Call ended", clear-on-new-
 * turn) keeps using `setReply` and behaves exactly as before because the
 * queue's `setReply` flushes pending chunks before overwriting.
 *
 * Pacing logic + tests live in `web/src/utils/replyQueue.js`. This file
 * is just the React glue.
 */
import { useEffect, useRef, useState } from 'react';
import { createReplyQueue } from '../utils/replyQueue';

export default function useProgressiveReply(opts = {}) {
  const [reply, setReplyState] = useState('');
  const queueRef = useRef(null);

  // Lazy-init so we don't create a new queue on every render. The queue
  // outlives renders; React state via `setReplyState` drives re-renders.
  if (queueRef.current === null) {
    queueRef.current = createReplyQueue({
      onChange: setReplyState,
      ...opts,
    });
  }

  // Cancel any in-flight timer when the host component unmounts.
  useEffect(() => {
    const q = queueRef.current;
    return () => {
      if (q) q.dispose();
    };
  }, []);

  return {
    reply,
    setReply: (text) => queueRef.current.setReply(text),
    enqueueChunk: (text) => queueRef.current.enqueue(text),
    // #1032 — reveal any still-queued sentences at once. The host calls
    // this when the companion stops speaking so the full reply is there
    // to re-read rather than trailing in after her voice.
    flushReply: () => queueRef.current.flush(),
  };
}
